import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SearchBox from "@/components/feature/SearchBox";
import MovieCard from "@/components/feature/MovieCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import usePageMeta from "@/hooks/usePageMeta";
import { getHotMovies, searchMovies } from "@/utils/movie";

export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const keyword = (searchParams.get("q") ?? "").trim();

  usePageMeta({
    title: keyword
      ? `“${keyword}”搜索结果 - 激情丛林高清影视在线观看`
      : "搜索影视 - 激情丛林高清在线观看",
    description:
      "在激情丛林影视内容站搜索“激情丛林”及相关影视作品，支持按剧名、简介、类型与演员快速查找高清影视资源。",
    keywords: "激情丛林搜索结果,影视搜索,激情丛林在线观看,高清影视",
  });

  const results = useMemo(() => (keyword ? searchMovies(keyword) : []), [keyword]);
  const hotPicks = getHotMovies(4);

  return (
    <div className="px-4 md:px-6 py-6 md:py-10">
      <div className="max-w-7xl mx-auto">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索" }]} />

        <div className="mt-5 mb-8 max-w-2xl">
          <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-foreground-950 mb-4">
            搜索影视内容
          </h1>
          <SearchBox variant="page" initialValue={keyword} />
          <p className="mt-3 text-sm text-foreground-500">
            支持按“激情丛林”剧名、简介、类型与演员模糊搜索
          </p>
        </div>

        {keyword ? (
          <div>
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-background-200">
              <h2 className="font-heading text-lg font-bold text-foreground-950">
                “{keyword}” 的搜索结果
              </h2>
              <span className="text-sm text-accent-700 bg-accent-100 px-2.5 py-0.5 rounded-full font-semibold">
                共 {results.length} 条
              </span>
            </div>

            {results.length === 0 ? (
              <div className="py-16 text-center bg-background-50 rounded-lg border border-background-200">
                <span className="inline-flex w-16 h-16 items-center justify-center rounded-full bg-background-100 text-foreground-300 mb-4">
                  <i className="ri-search-eye-line text-3xl" aria-hidden="true"></i>
                </span>
                <p className="text-foreground-800 font-semibold">未找到相关内容</p>
                <p className="mt-1 text-sm text-foreground-400">
                  试试其他关键词，或查看下方热门推荐
                </p>
                <div className="mt-8 max-w-4xl mx-auto px-4">
                  <p className="text-left text-sm font-bold text-foreground-700 mb-4">热门推荐</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6">
                    {hotPicks.map((movie) => (
                      <MovieCard key={movie.id} movie={movie} />
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
                {results.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="py-10">
            <p className="text-foreground-400 text-sm mb-8">
              还没有输入关键词，下面是当前热门影视内容，点击卡片即可查看详情
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
              {getHotMovies(10).map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/category/tv"
                className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold px-5 py-2.5 rounded-md cursor-pointer whitespace-nowrap transition-colors"
              >
                <i className="ri-tv-2-line" aria-hidden="true"></i>
                浏览电视剧分类
              </Link>
              <Link
                to="/category/movie"
                className="inline-flex items-center gap-2 border border-background-300 hover:border-primary-400 text-foreground-700 text-sm font-semibold px-5 py-2.5 rounded-md cursor-pointer whitespace-nowrap transition-colors"
              >
                <i className="ri-film-line" aria-hidden="true"></i>
                浏览电影分类
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}